/**
 * ListingFeeDialog / ListingPublishDialog
 *
 * Paying to publish (or renew) a listing with clear fee consent and inline card capture
 * (BR-88..BR-97, JR-8). One flow serves both modes; RenewListingDialog wraps it for renewal.
 *
 *   loading        skeleton while the server quote (and unit timezone) resolve
 *   card step      no saved card (or "Use a different card"): CardSetupFlow, nothing is charged
 *   summary        fee box + card line + policy note + consent; primary disabled until ticked
 *   A charging     every control disabled, one request in flight, "Charging <card>…"
 *   B declined     reason, "Nothing was charged", listing unchanged; "Use a different card"
 *   D free         unit already paid: no fee box, no consent. A first publish (draft) asks for a plain confirm;
 *                  re-activating a paused listing has nothing to confirm and publishes as soon as the quote says free
 *   E in progress  CHARGE_IN_PROGRESS (another tab): wait + Refresh, never double-charged
 *   F not offered  LISTING_FEE_NOT_OFFERED: "Listings aren't part of your plan"
 *
 *   H contact info LISTING CONTACT_INFO_REQUIRED: the listing lacks a contact name/phone — rejected BEFORE any
 *                  charge: "Nothing was charged" + a way to add it (never a silent close)
 *
 *   I paid, not live  the charge succeeded (200, published:false) but the publish failed afterwards (BR-110): the
 *                  payment is SAFE — say so, say what to fix, and offer "Publish again" (free; the unit is paid)
 *                  or "Add contact info". Never says the listing is live.
 *
 *   H copy branches on the quote: "Nothing was charged" only when the unit has no current paid period; an
 *   already-paid unit gets "Your payment is safe — one more thing to fix".
 *
 *   G unreconciled LISTING_FEE_UNRECONCILED (BR-91): an earlier attempt is being confirmed; never invite a
 *                  second charge — "try again shortly or contact support"
 *
 * The idempotency key is persisted per listing and mode (lib/charge-idempotency) so a page reload or
 * a reopened dialog retries an UNRESOLVED attempt (network error, 5xx, CHARGE_IN_PROGRESS, unreconciled)
 * under the same key — the billing subsystem de-duplicates it (BR-91). Any definitive outcome (paid,
 * declined, consent mismatch, ...) rotates it: a reused key would replay the old decline.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CONSENT_COPY } from '@resida/shared';
import {
  getPublishQuote,
  activateListing,
  renewListing,
  type Listing,
} from '../../lib/leasing.api';
import { formatUsd, formatPeriodDate } from '../../api/billing';
import { peekChargeIdempotencyKey } from '../../lib/charge-idempotency';
import { useUnitTimezone } from '../../hooks/useUnitTimezone';
import type { ActivateListingResponse, PublishFailure, RenewListingResponse } from '@resida/api-types/leasing';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  ChargeActionButton,
  ChargeCancelButton,
  ChargeCardCapture,
  ChargeCardLine,
  ChargeConsent,
  ChargeDeclinedPanel,
  ChargeDialogFooter,
  ChargeDialogShell,
  ChargeInProgressPanel,
  ChargeOutcomeAlerts,
  ChargeQuoteErrorPanel,
  ChargingStatus,
  CHARGE_BUTTON_CLASS,
} from '../billing/ChargeDialogParts';
import { PaymentAlert } from '../billing/CardSetupFlow';
import { useChargeFlow } from '../billing/useChargeFlow';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type ListingFeeMode = 'publish' | 'renew';

/** The outcome specific to the listing-fee route, on top of the shared charge outcomes. */
type ListingRouteOutcome = { kind: 'not_offered' } | { kind: 'contact_required' };

/** The charge succeeded but the publish did not (BR-110): kept beside the flow outcome, it is not an error. */
type PaidNotPublished = { kind: 'paid_not_published'; failure: PublishFailure };

const FALLBACK_PUBLISH_FAILURE: PublishFailure = {
  code: 'ACTIVATION_FAILED',
  message: "We couldn't publish this listing after your payment.",
};

interface ListingFeeDialogProps {
  mode: ListingFeeMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: Pick<Listing, 'id' | 'unitId' | 'status'>;
  /** "Unit 2B" — used in sentences. */
  unitName: string;
  /** "Unit 2B · 412 Oak Street" — the dialog's sub-title. */
  unitLabel: string | null;
  /**
   * The charge (or free republish) succeeded; `message` is ready to show the landlord and `published` is
   * the listing as the server returned it, so a parent can seed its cache before navigating.
   */
  onCompleted: (message: string, published: Listing) => void;
  /**
   * The publish was refused before any charge because the listing has no contact name/phone. The dialog
   * explains it; this lets the parent take the landlord to the fields (default: a link to the listing).
   */
  onContactInfoRequired?: () => void;
  /**
   * The payment went through but the listing did not go live (200 with published:false). The dialog stays
   * open with the payment-safe panel; this lets the parent refresh so the Paid badge appears.
   */
  onPaidNotPublished?: () => void;
}

function SummaryRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex justify-between gap-3" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground text-right">{value}</span>
    </div>
  );
}

export function ListingFeeDialog({
  mode,
  open,
  onOpenChange,
  listing,
  unitName,
  unitLabel,
  onCompleted,
  onContactInfoRequired,
  onPaidNotPublished,
}: ListingFeeDialogProps) {
  const p = mode === 'publish' ? 'listing-publish' : 'listing-renew';
  const verb = mode === 'publish' ? 'publish' : 'renew';
  // E2E-only opt-in (same contract as CheckoutPage) — see the AddressElement options in CardSetupFlow.
  const [searchParams] = useSearchParams();
  const disableAddressAutocomplete = searchParams.get('disableAddressAutocomplete') === '1';

  const keyScope = `${p}:${listing.id}`;
  const quoteQuery = useQuery({
    queryKey: ['listing-publish-quote', listing.id],
    // The persisted key lets the server return the PINNED quote of an unresolved earlier attempt (BR-91).
    queryFn: () => getPublishQuote(listing.id, peekChargeIdempotencyKey(keyScope)),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });
  const { data: timezoneData, isPending: timezonePending, isError: timezoneError } = useUnitTimezone(
    open ? listing.unitId : null,
  );
  const timezone = timezoneError || !timezoneData ? 'UTC' : timezoneData;

  const flow = useChargeFlow<ListingRouteOutcome>({
    open,
    keyScope,
    onOpenChange,
    refetchQuote: () => void quoteQuery.refetch(),
    unreconciledCode: 'LISTING_FEE_UNRECONCILED',
    quotedCard: quoteQuery.data?.defaultPaymentMethod,
    classifyRouteError: (err) => {
      if (err.code === 'LISTING_FEE_NOT_OFFERED') return { kind: 'not_offered' };
      if (err.code === 'CONTACT_INFO_REQUIRED') return { kind: 'contact_required' };
      return undefined;
    },
  });
  const { consent, charging, outcome, card, close } = flow;
  const [paidNotPublished, setPaidNotPublished] = useState<PaidNotPublished | null>(null);
  useEffect(() => {
    if (!open) setPaidNotPublished(null);
  }, [open]);
  // The confirm button unmounts when the paid-not-published panel replaces the summary: move focus to the
  // panel so keyboard and screen-reader users are not dropped to the dialog root.
  const paidPanelRef = useRef<HTMLDivElement>(null);
  const showingPaidPanel = paidNotPublished !== null;
  useEffect(() => {
    if (showingPaidPanel) paidPanelRef.current?.focus();
  }, [showingPaidPanel]);

  const quote = quoteQuery.data;

  const periodDays = quote?.periodDays ?? null;
  const dateText = (iso: string) => formatPeriodDate(iso, timezone, true);
  const shortDate = (iso: string) => formatPeriodDate(iso, timezone);

  // Period the landlord is paying for: publish starts now, renew starts when the current one ends.
  const newPeriodStart =
    mode === 'renew' && quote?.currentPeriodEndsAt ? new Date(quote.currentPeriodEndsAt) : new Date();
  const newPeriodEnd =
    periodDays !== null ? new Date(newPeriodStart.getTime() + periodDays * MS_PER_DAY) : null;

  const requiresFee = mode === 'renew' || (quote?.requiresFee ?? true);
  const feeCents = quote?.feeCents ?? null;
  const notOffered = outcome?.kind === 'not_offered' || (requiresFee && quote !== undefined && feeCents === null);

  const handleCharge = () => {
    if (!quote) return;
    if (requiresFee && (feeCents === null || !consent)) return;
    void flow.charge<ActivateListingResponse | RenewListingResponse>(
      (key) =>
        mode === 'renew'
          ? renewListing(listing.id, { consentToFeeCents: feeCents ?? 0, idempotencyKey: key })
          : activateListing(listing.id, {
              ...(requiresFee ? { consentToFeeCents: feeCents ?? 0 } : {}),
              idempotencyKey: key,
            }),
      (result) => {
        // Absent `published` (renew, or an older server) means published.
        if ('published' in result && !result.published) {
          setPaidNotPublished({ kind: 'paid_not_published', failure: result.publishFailure ?? FALLBACK_PUBLISH_FAILURE });
          onPaidNotPublished?.();
          return;
        }
        onCompleted(completionMessage(result.listing), result.listing);
      },
    );
  };

  // Re-activating a paused listing on a paid unit costs nothing and changes nothing the landlord has not already
  // asked for by clicking Re-activate / Republish: publish once per open as soon as the quote says it is free.
  // A failed attempt leaves the plain confirm (with the error) for a manual retry.
  const republishWithoutConfirm = mode === 'publish' && listing.status === 'paused' && quote !== undefined && !requiresFee;
  const autoPublishedRef = useRef(false);
  useEffect(() => {
    if (!open) autoPublishedRef.current = false;
  }, [open]);
  useEffect(() => {
    if (
      republishWithoutConfirm &&
      flow.sessionFresh &&
      !timezonePending &&
      !paidNotPublished &&
      !notOffered &&
      !charging &&
      outcome === null &&
      !autoPublishedRef.current
    ) {
      autoPublishedRef.current = true;
      handleCharge();
    }
  });

  /** "Publish again": re-read the quote (the unit is paid now, so it is free) and publish it again. */
  const publishAgain = async () => {
    await quoteQuery.refetch();
    autoPublishedRef.current = false;
    setPaidNotPublished(null);
  };

  function completionMessage(published: Listing): string {
    if (mode === 'renew') {
      return newPeriodEnd
        ? `${unitName} renewed — live until ${dateText(newPeriodEnd.toISOString())}. Receipt emailed. Find it under Settings → Billing.`
        : `${unitName} renewed. Receipt emailed. Find it under Settings → Billing.`;
    }
    const endsAt = published.paidPeriod?.endsAt ?? newPeriodEnd?.toISOString() ?? null;
    if (listing.status === 'draft') {
      // First publish (BR-111): the notice's own "Published on Resida. Now get it seen." heading leads, and the message never
      // names the unit or its address.
      const live = endsAt ? `Live until ${dateText(endsAt)}.` : 'Live now.';
      return requiresFee ? `${live} Receipt emailed. Find it under Settings → Billing.` : live;
    }
    const until = endsAt ? ` until ${dateText(endsAt)}` : '';
    return requiresFee
      ? `${unitName} is live${until}. Receipt emailed. Find it under Settings → Billing.`
      : `${unitName} is live again${until}.`;
  }

  // --------------------------------------------------------------------------------------
  // Content
  // --------------------------------------------------------------------------------------

  /** "Add contact info": the parent's hand-off when it has one, else a link to the listing. */
  const renderAddContact = () =>
    onContactInfoRequired ? (
      <Button type="button" className={CHARGE_BUTTON_CLASS} data-testid={`${p}-add-contact`} onClick={onContactInfoRequired}>
        Add contact info
      </Button>
    ) : (
      <Button asChild className={CHARGE_BUTTON_CLASS}>
        <Link to={`/leasing/listings/${listing.id}`} data-testid={`${p}-add-contact`}>
          Add contact info
        </Link>
      </Button>
    );

  const renderBody = (): { title: string; description: string | null; body: React.ReactNode } => {
    if (quoteQuery.isError) {
      return {
        title: mode === 'publish' ? 'Publish this listing' : `Renew ${unitName}`,
        description: unitLabel,
        body: (
          <ChargeQuoteErrorPanel
            testIdPrefix={p}
            message="We couldn't load the price for this listing. Try again in a moment."
            onClose={close}
            onRetry={() => void quoteQuery.refetch()}
          />
        ),
      };
    }

    if (!quote || timezonePending || !flow.sessionFresh) {
      return {
        title: mode === 'publish' ? 'Publish this listing' : `Renew ${unitName}`,
        description: unitLabel,
        body: (
          <div className="flex flex-col gap-3" data-testid={`${p}-loading`} aria-busy="true">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-11 w-full" />
          </div>
        ),
      };
    }

    // I — paid, but the listing did not go live.
    if (paidNotPublished) {
      const { failure } = paidNotPublished;
      return {
        title: "Payment received — your listing isn't live yet",
        description: unitLabel,
        body: (
          <div
            ref={paidPanelRef}
            tabIndex={-1}
            className="flex flex-col gap-4 outline-none"
            data-testid={`${p}-paid-not-published`}
          >
            <PaymentAlert tone="warning" testId={`${p}-paid-not-published-message`}>
              <p className="m-0 font-medium">{failure.message}</p>
              <p className="m-0">Your payment is safe. Fix this, then publish again at no charge.</p>
            </PaymentAlert>
            <ChargeDialogFooter>
              <ChargeCancelButton testId={`${p}-close`} label="Close" onClick={close} />
              {failure.code === 'CONTACT_INFO_REQUIRED' ? (
                renderAddContact()
              ) : (
                <ChargeActionButton
                  testId={`${p}-publish-again`}
                  busy={quoteQuery.isFetching}
                  busyLabel="Checking…"
                  disabled={quoteQuery.isFetching}
                  onClick={() => void publishAgain()}
                >
                  Publish again
                </ChargeActionButton>
              )}
            </ChargeDialogFooter>
          </div>
        ),
      };
    }

    // F — the plan doesn't offer listings.
    if (notOffered) {
      return {
        title: "Listings aren't part of your plan",
        description: `Your current plan doesn't include publishing listings. Switch plans to ${verb} ${unitName}${mode === 'publish' ? ' — your draft is saved' : ''}.`,
        body: (
          <ChargeDialogFooter testId={`${p}-not-offered`}>
            <ChargeCancelButton testId={`${p}-close`} label="Close" onClick={close} />
            <Button asChild className={CHARGE_BUTTON_CLASS}>
              <Link to="/settings/billing" data-testid={`${p}-compare-plans`}>
                Compare plans
              </Link>
            </Button>
          </ChargeDialogFooter>
        ),
      };
    }

    // H — publish was refused before any charge: the listing needs a contact name and phone.
    if (outcome?.kind === 'contact_required') {
      return {
        title: 'Add contact info to publish',
        description: unitLabel,
        body: (
          <div className="flex flex-col gap-4" data-testid={`${p}-contact-required`}>
            <PaymentAlert tone="warning" testId={`${p}-contact-required-message`}>
              <p className="m-0 font-medium">This listing needs a contact name and phone before it can go live.</p>
              <p className="m-0">
                {quote.requiresFee
                  ? 'Nothing was charged. Add them, then publish again.'
                  : 'Your payment is safe — one more thing to fix. Add them, then publish again at no charge.'}
              </p>
            </PaymentAlert>
            <ChargeDialogFooter>
              <ChargeCancelButton testId={`${p}-close`} label="Close" onClick={close} />
              {renderAddContact()}
            </ChargeDialogFooter>
          </div>
        ),
      };
    }

    // D — the unit is already paid: no charge. A paused listing publishes without a confirm; anything else confirms.
    if (!requiresFee) {
      if (republishWithoutConfirm && outcome === null) {
        return {
          title: `Publishing ${unitName}`,
          description: unitLabel,
          body: (
            <p role="status" className="m-0 text-sm text-muted-foreground" data-testid={`${p}-publishing-status`}>
              {unitName} is already paid, so there is no charge. Publishing…
            </p>
          ),
        };
      }
      const paidThrough = quote.currentPeriodEndsAt ? dateText(quote.currentPeriodEndsAt) : null;
      return {
        title: listing.status === 'paused' ? `Publish ${unitName} again?` : `Publish ${unitName}?`,
        description: `${unitName} is already paid${paidThrough ? ` through ${paidThrough}` : ''}, so there's no charge. Its Resida listing page goes live again right away.`,
        body: (
          <div className="flex flex-col gap-3">
            <ChargeOutcomeAlerts testIdPrefix={p} outcome={outcome} />
            <ChargeDialogFooter>
              <ChargeCancelButton testId={`${p}-cancel`} label="Cancel" onClick={close} disabled={charging} />
              <ChargeActionButton
                testId={`${p}-confirm`}
                busy={charging}
                busyLabel="Publishing…"
                disabled={charging}
                onClick={handleCharge}
              >
                Publish
              </ChargeActionButton>
            </ChargeDialogFooter>
          </div>
        ),
      };
    }

    const feeText = feeCents !== null ? formatUsd(feeCents) : '';

    // Card capture — no saved card, or the landlord chose another one.
    if (flow.capturingCard || !card) {
      return {
        title: `Add a card to ${verb}`,
        description: unitLabel,
        body: (
          <ChargeCardCapture
            testIdPrefix={p}
            disableAddressAutocomplete={disableAddressAutocomplete}
            handlers={flow.cardCapture}
            summary={
              <p className="text-sm text-muted-foreground m-0 mb-4">
                Nothing is charged yet. We save your card, then ask you to confirm the {feeText} listing fee.
              </p>
            }
          />
        ),
      };
    }

    // B — declined.
    if (outcome?.kind === 'declined') {
      return {
        title: mode === 'publish' ? 'Publish this listing' : `Renew ${unitName}`,
        description: unitLabel,
        body: (
          <ChargeDeclinedPanel
            testIdPrefix={p}
            reason={outcome.reason}
            unchangedNote={
              mode === 'renew'
                ? "your listing's end date hasn't changed."
                : `the listing is still ${listing.status === 'paused' ? 'paused' : 'a draft'}.`
            }
            onClose={close}
            onUseDifferentCard={flow.startCardCapture}
          />
        ),
      };
    }

    // E — a charge for this unit is already being processed.
    if (outcome?.kind === 'in_progress') {
      return {
        title: mode === 'publish' ? 'Publish this listing' : `Renew ${unitName}`,
        description: unitLabel,
        body: (
          <ChargeInProgressPanel testIdPrefix={p} closeTestId={`${p}-close`} onClose={close}>
            A payment for {unitName} is already being processed, maybe in another tab. Wait a moment and refresh —
            you won&apos;t be charged twice.
          </ChargeInProgressPanel>
        ),
      };
    }

    // Summary + consent (with the A "charging" state layered on the same layout).
    const endsText = newPeriodEnd ? dateText(newPeriodEnd.toISOString()) : null;
    const consentCopy = mode === 'renew' ? CONSENT_COPY.listing_fee_renewal(feeText) : CONSENT_COPY.listing_fee(feeText);
    const confirmLabel = `Pay ${feeText} & ${verb}`;

    return {
      title:
        mode === 'publish'
          ? 'Publish this listing'
          : `Keep ${unitName} live${periodDays ? ` for ${periodDays} more days` : ''}`,
      description: unitLabel,
      body: (
        <div className="flex flex-col gap-5">
          <div
            className="rounded-lg border border-border p-4 flex flex-col gap-2.5 text-sm"
            data-testid={`${p}-fee-box`}
          >
            {mode === 'publish' ? (
              <>
                <SummaryRow label="Listing fee" value={`${feeText} + tax`} testId={`${p}-fee`} />
                <SummaryRow
                  label="Live for"
                  value={`${periodDays} days${endsText ? ` · until ${endsText}` : ''}`}
                  testId={`${p}-period`}
                />
                <SummaryRow label="AI leasing agent" value="Included" testId={`${p}-agent`} />
              </>
            ) : (
              <>
                <SummaryRow
                  label="Current period ends"
                  value={quote.currentPeriodEndsAt ? shortDate(quote.currentPeriodEndsAt) : '—'}
                  testId={`${p}-current-end`}
                />
                <SummaryRow
                  label="New period"
                  value={
                    quote.currentPeriodEndsAt && newPeriodEnd
                      ? `${shortDate(quote.currentPeriodEndsAt)} – ${shortDate(newPeriodEnd.toISOString())}`
                      : '—'
                  }
                  testId={`${p}-new-period`}
                />
                <SummaryRow label="Listing fee" value={`${feeText} + tax`} testId={`${p}-fee`} />
              </>
            )}
          </div>

          {quote.resumesEarlierAttempt && (
            <p role="status" className="m-0 text-sm text-muted-foreground" data-testid={`${p}-resumes-note`}>
              This resumes your earlier attempt, so the fee and period above are the ones from that attempt. The same
              request can&apos;t charge you twice.
            </p>
          )}

          {outcome?.kind === 'price_changed' && (
            <p role="status" className="m-0 text-sm text-warning" data-testid={`${p}-fee-changed`}>
              The listing fee has changed{outcome.feeCents !== null ? ` to ${formatUsd(outcome.feeCents)}` : ''}. Review
              the amount and confirm again.
            </p>
          )}

          <ChargeCardLine
            testIdPrefix={p}
            card={card}
            charging={charging}
            changeCardTestId={`${p}-use-different-card`}
            onChangeCard={flow.changeCard}
          />

          {mode === 'publish' && newPeriodEnd && (
            <p className="m-0 text-[13px] leading-5 text-muted-foreground" data-testid={`${p}-policy`}>
              Pausing, closing or relisting {unitName} before {shortDate(newPeriodEnd.toISOString())} is free. The fee
              isn&apos;t refunded if the unit fills early. Nothing renews automatically — we&apos;ll remind you 3 days
              before it ends.
            </p>
          )}
          {mode === 'renew' && (
            <p className="m-0 text-[13px] leading-5 text-muted-foreground" data-testid={`${p}-policy`}>
              The new period starts when the current one ends, so no days are lost. The fee isn&apos;t refunded if the
              unit fills early. Nothing renews automatically.
            </p>
          )}

          <ChargeOutcomeAlerts testIdPrefix={p} outcome={outcome} />

          <ChargeConsent testId={`${p}-consent`} checked={consent} disabled={charging} onChange={flow.setConsent}>
            {consentCopy}
          </ChargeConsent>

          {charging && <ChargingStatus testIdPrefix={p} card={card} />}

          <ChargeDialogFooter>
            <ChargeCancelButton
              testId={`${p}-cancel`}
              label={mode === 'renew' ? 'Not now' : 'Cancel'}
              onClick={close}
              disabled={charging}
            />
            <ChargeActionButton
              testId={`${p}-confirm`}
              busy={charging}
              busyLabel="Charging…"
              disabled={!consent || charging}
              onClick={handleCharge}
            >
              {confirmLabel}
            </ChargeActionButton>
          </ChargeDialogFooter>
        </div>
      ),
    };
  };

  const { title, description, body } = renderBody();

  return (
    <ChargeDialogShell
      open={open}
      onOpenChange={flow.guardedOpenChange}
      charging={charging}
      isChargeInFlight={flow.isChargeInFlight}
      testId={`${p}-dialog`}
      title={title}
      description={description}
    >
      {body}
    </ChargeDialogShell>
  );
}

/** Publish (or republish) a listing — see ListingFeeDialog for the states. */
export function ListingPublishDialog(props: Omit<ListingFeeDialogProps, 'mode'>) {
  return <ListingFeeDialog mode="publish" {...props} />;
}
